import React from 'react';
import { compose } from 'redux';
import { number, object, string } from 'prop-types';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { capitalizeFirstLetter, getUserTier } from '../../util/data';
import classNames from 'classnames';

import staffImg from './img/badgeStaff.svg';
import betaTesterImg from './img/badgeBetaTester.svg';
import topReviewerImg from './img/badgeTopReviewer.svg';
import yearsImg from './img/badgeYears.svg';
import Icon from '../Icon/Icon';

import css from './Badges.module.css';

// Badge constants
export const BADGE_STUFF = 'stuff';
export const BADGE_BETA_TESTER = 'beta-tester';
export const BADGE_TOP_REVIEWER = 'top-reviewer';
export const BADGE_YEARS = 'years';
export const BADGE_TIER = 'tier';

// Export badge images and icons for reuse
export const BadgeStuff = props => <img src={staffImg} {...props} />;
export const BadgeBetaTester = props => <img src={betaTesterImg} {...props} />;
export const BadgeTopReviewer = props => <img src={topReviewerImg} {...props} />;
export const BadgeYears = props => <img src={yearsImg} {...props} />;
// A level (tier) is the board's star in the level's colour (MemberProfile: "★ INSIDER"). The span
// keeps the hover tooltip the badge images have; the icon carries the name for screen readers.
export const BadgeTier = props => {
  const { className, tierId, title, size } = props;
  return (
    <span className={css.tier} title={title}>
      <Icon
        name="tier"
        size={size}
        filled
        title={title}
        className={classNames(css[`tier${capitalizeFirstLetter(tierId)}`], className)}
      />
    </span>
  );
};

BadgeTier.defaultProps = { className: null, title: null, size: 18 };

BadgeTier.propTypes = {
  className: string,
  tierId: string.isRequired,
  title: string,
  size: number,
};

const badgeImages = {
  [BADGE_STUFF]: BadgeStuff,
  [BADGE_BETA_TESTER]: BadgeBetaTester,
  [BADGE_TOP_REVIEWER]: BadgeTopReviewer,
  [BADGE_YEARS]: BadgeYears,
};

const badgeAltKeys = {
  [BADGE_STUFF]: 'Badges.stuff',
  [BADGE_BETA_TESTER]: 'Badges.betaTester',
  [BADGE_TOP_REVIEWER]: 'Badges.topReviewer',
  [BADGE_YEARS]: 'Badges.years',
};

// BadgeImage component that renders images or tier icons dynamically
const BadgeImage = ({ badgeId, alt, ...props }) => {
  const BadgeComponent = badgeImages[badgeId];
  return BadgeComponent ? <BadgeComponent alt={alt} title={alt} {...props} /> : null;
};

// Main Badges component
const Badges = props => {
  const { rootClassName, className, iconClassName, iconSize, imageClassName, intl, user } = props;

  const badges = user?.attributes?.profile?.metadata?.badges || [];
  const points = user?.attributes?.profile?.metadata?.points;
  const userTier = getUserTier(points);

  if (!badges.length && !userTier) {
    return null;
  }

  const renderTierIcon = userTier && (
    <BadgeTier
      className={iconClassName}
      size={iconSize}
      tierId={userTier.id}
      title={capitalizeFirstLetter(userTier.id)}
    />
  );

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      {renderTierIcon}
      {badges.map(badge => {
        const { id: badgeId } = badge;
        const altText = intl.formatMessage({ id: badgeAltKeys[badgeId] });
        return (
          <BadgeImage
            key={badgeId}
            badgeId={badgeId}
            alt={altText}
            className={classNames(css.badgeImage, imageClassName)}
          />
        );
      })}
    </div>
  );
};

Badges.defaultProps = {
  rootClassName: null,
  className: null,
  iconClassName: null,
  iconSize: 18,
  imageClassName: null,
  user: null,
};

Badges.propTypes = {
  rootClassName: string,
  className: string,
  iconClassName: string,
  // The level star's size, one of the Icon sizes, so it stays square and drawn at 1.5px
  iconSize: number,
  imageClassName: string,
  user: object.isRequired,
  intl: intlShape.isRequired,
};

export default compose(injectIntl)(Badges);
